import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, IconButton, Menu, MenuItem, Typography, Checkbox, ListItemText, Divider, TableCell,
} from '@mui/material';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { InfoableButton, type ButtonInfo } from './ImportResolutionBadge';
import { BRAND } from '../theme/tokens';
import { SALES_DOCUMENT_META, type SalesDocumentKind } from '../config/salesDocumentLineColumns';

export interface LineTableColumnDef {
  id: string;
  label: string;
  /** Cannot be hidden — e.g. Description, Qty */
  locked?: boolean;
  hint?: string;
}

const COLUMN_SETTINGS_INFO: ButtonInfo = {
  title: 'Line table columns',
  description:
    'Choose which columns appear on this document\'s line table while you edit. ' +
    'Supplier codes come from the supplier — type them, pick from the price book, or use + New supplier code. ' +
    'Inventory codes are your company codes (FIE-234). Use Auto-set to mint the next prefix number. ' +
    'The Supplier Code column only appears when “Show Supplier Code column” is ON. ' +
    'Use Document Settings for org-wide editing defaults, or Template Settings to control columns on the printed PDF.',
  example:
    'On PO-1648 hide Discount and Job for a cleaner supplier-facing view. Turn off “Show Supplier Code column” in the footer to hide supplier codes on all POs/Bills.',
};

const SALES_SYSTEM_FIELDS_INFO: ButtonInfo = {
  title: 'Sales system fields',
  description:
    'System fields are the columns available on every sales document — Invoice, Quote, ' +
    'Purchase Order and Bill. This is how you update the columns shown on all sales documents: ' +
    'turn a field on or off to control what appears on the line table while you edit. ' +
    'Document Settings sets the default for new documents; Template Settings controls what prints on the PDF.',
  example:
    'Hide Discount and Job on a Purchase Order for a cleaner supplier-facing view, while keeping them on ' +
    'Invoices. Supplier Code stays available on POs and Bills so codes can be matched and saved to the price book.',
};

export interface LineTableColumnSettingsProps {
  columns: LineTableColumnDef[];
  visible: Record<string, boolean>;
  onToggle: (id: string) => void;
  /** PO, Bill, Quote, or Invoice — enables Document / Template Settings links */
  documentKind: SalesDocumentKind;
  /** Current template name — passed through to Template Settings */
  templateName?: string;
  /** URL to return from settings pages */
  returnTo?: string;
}

const settingsLinkSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  px: 1.5,
  py: 0.85,
  fontSize: '0.76rem',
  fontWeight: 700,
  color: BRAND,
  textDecoration: 'none',
  '&:hover': { backgroundColor: '#f8f4ff' },
} as const;

export const LineTableColumnSettings: React.FC<LineTableColumnSettingsProps> = ({
  columns, visible, onToggle, documentKind, templateName, returnTo,
}) => {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const open = Boolean(anchor);
  const meta = SALES_DOCUMENT_META[documentKind];
  const returnParam = returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : '';
  const templateParam = templateName
    ? `${returnParam ? '&' : '?'}template=${encodeURIComponent(templateName)}`
    : '';
  const docSettingsHref = `${meta.documentSettingsPath}${returnParam}`;
  const templateSettingsHref = `${meta.templateSettingsPath}${returnParam}${templateParam}`;

  return (
    <TableCell
      className="no-print"
      sx={{
        width: 40, px: 0.5, py: 0.5, verticalAlign: 'middle',
        borderBottom: '1px solid #D8D8DC',
        backgroundColor: '#F0F0F2',
      }}
    >
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
        <InfoableButton info={COLUMN_SETTINGS_INFO}>
          <IconButton
            size="small"
            aria-label="Line table column settings"
            onClick={e => setAnchor(e.currentTarget)}
            sx={{
              width: 26, height: 26,
              color: '#6F6489',
              border: '1px solid #e0d5f5',
              backgroundColor: '#faf8ff',
              '&:hover': { color: BRAND, borderColor: BRAND, backgroundColor: '#f3edfc' },
            }}
          >
            <SettingsOutlinedIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </InfoableButton>
      </Box>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 260, maxWidth: 320 } }}
      >
        <Box sx={{ px: 1.5, pt: 1, pb: 0.25, display: 'flex', alignItems: 'center', gap: 0.6 }}>
          <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: BRAND, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Sales — System Fields
          </Typography>
          <InfoableButton info={SALES_SYSTEM_FIELDS_INFO}>
            <InfoOutlinedIcon sx={{ fontSize: 14, color: '#aaa', cursor: 'help', display: 'block' }} />
          </InfoableButton>
        </Box>
        <Box sx={{ px: 1.5, pb: 0.5 }}>
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Show columns (while editing)
          </Typography>
        </Box>
        {columns.map(col => (
          <MenuItem
            key={col.id}
            dense
            disabled={col.locked}
            onClick={() => !col.locked && onToggle(col.id)}
            sx={{ py: 0.4 }}
          >
            <Checkbox
              size="small"
              checked={col.locked || visible[col.id] !== false}
              disabled={col.locked}
              sx={{ p: 0.5, mr: 0.5, '&.Mui-checked': { color: BRAND } }}
            />
            <ListItemText
              primary={col.label}
              secondary={col.hint}
              primaryTypographyProps={{ fontSize: '0.78rem', fontWeight: 600 }}
              secondaryTypographyProps={{ fontSize: '0.65rem' }}
            />
          </MenuItem>
        ))}
        <Divider sx={{ my: 0.5 }} />
        <Typography sx={{ px: 1.5, py: 0.75, fontSize: '0.65rem', color: '#888', lineHeight: 1.45 }}>
          Change here what you see while {meta.editPhrase}. You can also update{' '}
          <strong>Templates</strong> to show or hide different columns on the printed document.
        </Typography>
        <Divider sx={{ my: 0.5 }} />
        <Box
          component={RouterLink}
          to={docSettingsHref}
          onClick={() => setAnchor(null)}
          sx={settingsLinkSx}
        >
          Document Settings
          <OpenInNewIcon sx={{ fontSize: 14, opacity: 0.7 }} />
        </Box>
        <Box
          component={RouterLink}
          to={templateSettingsHref}
          onClick={() => setAnchor(null)}
          sx={settingsLinkSx}
        >
          Template Settings
          <OpenInNewIcon sx={{ fontSize: 14, opacity: 0.7 }} />
        </Box>
        {meta.showSupplierCodeNote && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <Typography sx={{ px: 1.5, py: 0.75, fontSize: '0.65rem', color: '#888', lineHeight: 1.45 }}>
              Supplier codes are never auto-created — they come from the supplier. Use{' '}
              <strong>Auto-create inventory code</strong> on the Inventory Code column when a supplier code is set.
            </Typography>
          </>
        )}
      </Menu>
    </TableCell>
  );
};

/** Load column visibility from localStorage; missing keys default to visible. */
export function loadColumnVisibility(storageKey: string, columnIds: string[]): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return Object.fromEntries(columnIds.map(id => [id, true]));
    const parsed = JSON.parse(raw) as Record<string, boolean>;
    return Object.fromEntries(columnIds.map(id => [id, parsed[id] !== false]));
  } catch {
    return Object.fromEntries(columnIds.map(id => [id, true]));
  }
}

export function saveColumnVisibility(storageKey: string, visible: Record<string, boolean>) {
  try { localStorage.setItem(storageKey, JSON.stringify(visible)); } catch { /* noop */ }
}

export default LineTableColumnSettings;
